import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";
import {
  getCommentCountByThreadIdFromLocalDB,
  getThreadCommentsByChatIdAndMessageIdFromLocalDB,
  updateMessageByServerIdInLocalDB,
} from "@/indexedDB/services/messages";
import { useNavigationState } from "@/zustand/NavigationState";
import MessageListItem from "./MessageListItem";
import { MessageCategory } from "@/server/types/message";
// import ServiceMessageListItem from "./ServiceMessageListItem";
import { memo, useRef, useEffect, useCallback } from "react";
import { MessageEntity } from "@/indexedDB/types";
import { getMessageGrouping } from "./utils/messageGrouping";
import { Button } from "@/components/primitives/button";
import { ChevronDownIcon } from "lucide-react";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";
import { useTranslation } from "react-i18next";
import { formatDateLabel } from "@/utils/dates";
import { areMessageItemPropsEqual } from "./utils/messageComparison";
import { useActiveThread } from "./hooks/useActiveThread";
import ThreadMessage from "./ThreadMessage";
import { useScrollToLatestMessage } from "./hooks/useScrollToLatestMessage";

const MessageItem = memo(function MessageItem({
  message,
  isFirstInGroup,
  isLastInGroup,
}: {
  message: MessageEntity;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
}) {
  if (message.category === MessageCategory.COMMENT) {
    return (
      <MessageListItem
        message={message}
        isFirstInGroup={isFirstInGroup}
        isLastInGroup={isLastInGroup}
      />
    );
  }
  return null;
}, areMessageItemPropsEqual);

/**
 * Memoized row wrapper that encapsulates the entire per-comment row JSX
 * (wrapper divs, date label, and MessageItem). This prevents parent state
 * changes (e.g., showScrollToBottom toggling from intersection observers)
 * from re-rendering every comment row.
 */
interface MemoizedCommentRowProps {
  message: MessageEntity;
  index: number;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  showDateLabel: boolean;
  currentMessageDate: string;
  /** Attached to the newest row (index 0) to drive scroll-button visibility */
  observedRef?: React.RefCallback<HTMLDivElement>;
}

function areMemoizedCommentRowPropsEqual(
  prev: MemoizedCommentRowProps,
  next: MemoizedCommentRowProps,
): boolean {
  return (
    prev.index === next.index &&
    prev.observedRef === next.observedRef &&
    prev.isFirstInGroup === next.isFirstInGroup &&
    prev.isLastInGroup === next.isLastInGroup &&
    prev.showDateLabel === next.showDateLabel &&
    prev.currentMessageDate === next.currentMessageDate &&
    areMessageItemPropsEqual(
      {
        message: prev.message,
        isFirstInGroup: prev.isFirstInGroup,
        isLastInGroup: prev.isLastInGroup,
      },
      {
        message: next.message,
        isFirstInGroup: next.isFirstInGroup,
        isLastInGroup: next.isLastInGroup,
      },
    )
  );
}

const MemoizedCommentRow = memo(function MemoizedCommentRow({
  message,
  index,
  isFirstInGroup,
  isLastInGroup,
  showDateLabel,
  currentMessageDate,
  observedRef,
}: MemoizedCommentRowProps) {
  return (
    <div ref={observedRef} data-index={index} data-message-id={message.id}>
      <div className="w-full lg:mx-auto lg:max-w-[1000px]">
        {showDateLabel && (
          <div className="mx-auto my-2 w-fit rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
            {currentMessageDate}
          </div>
        )}
        <MessageItem
          message={message}
          isFirstInGroup={isFirstInGroup}
          isLastInGroup={isLastInGroup}
        />
      </div>
    </div>
  );
}, areMemoizedCommentRowPropsEqual);

export default function ThreadCommentList() {
  const { t } = useTranslation();

  const activeThread = useActiveThread();

  // ------------
  // STATE & REFS
  // --------=---
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeThreadId = useNavigationState((state) => state.activeThreadId);

  // const activeChatMessageTake = useNavigationState(
  //   (state) => state.activeChatMessageTake,
  // );
  // const setActiveChatMessageTake = useNavigationState(
  //   (state) => state.setActiveChatMessageTake,
  // );

  // The thread message in IndexedDB may not have `commentTake` set yet
  // (it is only written by the pagination handler below). Fall back to the
  // default page size so the initial render and the "load older" increment
  // both work off a known number.
  const currentTake = activeThread?.commentTake ?? MESSAGE_LIST_PAGE_SIZE;

  // --------------
  // data fetching
  // --------=-----
  // Live query messages from IndexedDB
  const comments = useSafeLiveQuery(
    async () => {
      if (!activeChatId || !activeThreadId) {
        return null;
      }

      const _comments = await getThreadCommentsByChatIdAndMessageIdFromLocalDB(
        activeChatId,
        activeThreadId,
        currentTake,
      );

      return _comments ?? null;
    },
    [activeChatId, activeThreadId, currentTake],
    [],
  );

  // const comments = useSafeLiveQuery(
  //   () =>
  //     getThreadCommentsByChatIdAndMessageIdFromLocalDB(
  //       activeChatId || "",
  //       activeThreadId || "",
  //       activeThread?.commentTake || MESSAGE_LIST_PAGE_SIZE,
  //     ),
  //   [activeThreadId],
  //   [],
  // );

  const commentCount = useSafeLiveQuery(
    () => getCommentCountByThreadIdFromLocalDB(activeThreadId || ""),
    [activeThreadId],
    0,
  );

  /// refs
  const scrollerRef = useRef<HTMLDivElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);

  const [topAnchorRef, topAnchorEntry] = useIntersectionObserver({
    threshold: 0,
    root: scrollerRef.current,
    rootMargin: "0px",
  });

  // Derive both the newest signal AND the conversation key from the same
  // comments[0] object so a chat/thread switch can't seed the baseline from a
  // stale-data render (see MessageList for the full rationale). Comments are
  // queried by [chatId+threadId], so the newest comment's own ids identify the
  // active thread once its data has loaded.
  const newestComment = comments?.[0];
  const { latestRowRef, showScrollToBottom, scrollToLatestMessage } =
    useScrollToLatestMessage(scrollerRef, messagesListRef, {
      newestMessageId: newestComment
        ? (newestComment._clientId ?? newestComment.id)
        : null,
      newestIsOutgoing: newestComment?.isOutgoing ?? false,
      conversationKey: newestComment
        ? `${newestComment.chatId}:${newestComment.threadId}`
        : null,
    });

  // TO DO => extract to middleware reusable func
  const prependMessages = useCallback(async () => {
    if (!messagesListRef.current) {
      return;
    }

    if (!activeChatId || !activeThreadId) {
      return;
    }

    if (!activeThread) {
      return;
    }

    if (!comments || comments.length === 0) {
      return;
    }

    if (!activeThread.commentFirstPhaseMsgListInit) {
      return;
    }

    if (!commentCount) {
      return;
    }

    if (currentTake >= commentCount) {
      return;
    }

    await updateMessageByServerIdInLocalDB(activeThread.id, {
      commentTake: currentTake + MESSAGE_LIST_PAGE_SIZE,
    });
  }, [
    activeChatId,
    activeThreadId,
    comments,
    activeThread,
    commentCount,
    currentTake,
  ]);

  useEffect(() => {
    if (messagesListRef.current && activeThreadId) {
      messagesListRef.current.scrollTo({
        top: messagesListRef.current.scrollHeight,
      });
    }
  }, [messagesListRef, activeThreadId]);

  useEffect(() => {
    if (!topAnchorEntry?.isIntersecting) {
      return;
    }

    // If the top anchor is intersecting, we can load more messages
    prependMessages();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topAnchorEntry?.isIntersecting, commentCount]);

  if (!comments) {
    return null;
  }

  return (
    <div
      ref={scrollerRef}
      style={{ WebkitOverflowScrolling: "touch" }}
      className="relative size-full flex-1 flex-col justify-end overflow-y-auto overflow-x-hidden fade-in"
    >
      <div
        ref={messagesListRef}
        style={{ WebkitOverflowScrolling: "touch" }}
        className="custom-scroll custom-scroll-x relative flex size-full flex-col-reverse overflow-y-auto overflow-x-hidden overscroll-contain"
      >
        {/* Spacer: keeps the newest comment 4px off the container's bottom
            edge (first child = visual bottom in flex-col-reverse). */}
        <div className="h-1 w-full" />

        {comments.map((message: MessageEntity, index: number) => {
          const { isFirstInGroup, isLastInGroup } = getMessageGrouping(
            comments,
            index,
          );
          const currentMessageDate = formatDateLabel(message.timestamp);
          const previousMessageDate =
            index < comments.length - 1
              ? formatDateLabel(comments[index + 1].timestamp)
              : null;
          const showDateLabel = currentMessageDate !== previousMessageDate;

          return (
            <MemoizedCommentRow
              key={message._clientId}
              message={message}
              index={index}
              isFirstInGroup={isFirstInGroup}
              isLastInGroup={isLastInGroup}
              showDateLabel={showDateLabel}
              currentMessageDate={currentMessageDate}
              observedRef={index === 0 ? latestRowRef : undefined}
            />
          );
        })}

        {/* Top anchor for loading older messages */}
        <div ref={topAnchorRef} className="text-md w-full p-2 text-center">
          {!activeThread?.commentFullMsgListInit ? t("loading") : ""}
        </div>

        <div className="mb-auto w-full lg:mx-auto lg:max-w-[1000px]">
          {activeThread && <ThreadMessage message={activeThread} />}
        </div>
      </div>
      {showScrollToBottom && (
        <Button
          size="icon"
          className="absolute bottom-3 left-1/2 z-30 size-10 -translate-x-1/2 rounded-full bg-primary p-3 text-primary-foreground shadow-lg"
          onClick={() => scrollToLatestMessage()}
        >
          <ChevronDownIcon className="size-7" />
        </Button>
      )}
    </div>
  );
}
